import { gql, useApolloClient, useQuery } from '@apollo/client';
import {
  Avatar,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardHeaderControl,
  CardTitle,
} from '@apollo/orbit';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { AvatarModal } from 'src/components/common/uploadAvatar/AvatarModal';
import { URLModal } from 'src/components/common/URLModal';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

const UserAvatarCardQuery = gql<
  GraphQLTypes.UserAvatarCard,
  GraphQLTypes.UserAvatarCardVariables
>`
  query UserAvatarCard {
    me {
      id
      ... on User {
        fullName
        avatarUrl
        canUpdateAvatar
        avatarUpload {
          ... on MediaUploadInfo {
            csrfToken
            maxContentLength
            url
          }
        }
      }
    }
  }
`;

export const AvatarCard = () => {
  const { data } = useQuery(UserAvatarCardQuery, {
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });
  const location = useLocation();

  const avatarUpload =
    data?.me?.__typename === 'User' &&
    data.me?.avatarUpload?.__typename === 'MediaUploadInfo' &&
    data.me.avatarUpload;

  const user = data?.me?.__typename === 'User' && data.me;

  const client = useApolloClient();
  const uploadButtonDisabled = !(
    data?.me?.__typename === 'User' && data?.me.canUpdateAvatar
  );

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Avatar</CardTitle>
          <CardDescription>
            {data?.me && data.me.__typename === 'User' && !data.me.avatarUrl
              ? `You don't have an avatar image right now.`
              : 'Your current avatar image.'}
          </CardDescription>

          <CardHeaderControl>
            {data?.me &&
              data.me.__typename === 'User' &&
              !!data.me.avatarUrl && (
                <Avatar
                  variant="user"
                  size="sm"
                  src={
                    data.me.__typename === 'User'
                      ? (data.me.avatarUrl ?? '')
                      : ''
                  }
                />
              )}
            {data?.me && (
              <Tooltip
                disabled={!uploadButtonDisabled}
                label="Users on GitHub and SSO Accounts are not able to change their avatar."
              >
                <Button
                  variant="secondary"
                  isDisabled={uploadButtonDisabled}
                  as={Link}
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.updateUserAvatar,
                  })}
                >
                  Upload image
                </Button>
              </Tooltip>
            )}
          </CardHeaderControl>
        </CardHeader>
      </Card>
      {avatarUpload && user && (
        <URLModal name={Config.modals.updateUserAvatar}>
          {({ isOpen, closeModal }) => (
            <AvatarModal
              isOpen={isOpen}
              title="Update User Avatar"
              refreshAvatar={() => {
                client.cache.evict({
                  id: client.cache.identify({
                    __typename: user.__typename,
                    id: user.id,
                  }),
                  fieldName: 'avatarUrl',
                });
              }}
              onClose={closeModal}
              avatarUploadUrl={avatarUpload.url}
              avatarUploadToken={avatarUpload.csrfToken}
            />
          )}
        </URLModal>
      )}
    </>
  );
};
